import { useEffect, useRef, useState } from "react";
import { startReportGeneration } from "./report-generation";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { type ClassroomReportRow, US_TIMEZONES } from "./types";

export function GenerateDialog({
  classroomId,
  date,
  onClose,
  onDone,
}: {
  classroomId: number;
  date: string;
  onClose: () => void;
  onDone: (r: ClassroomReportRow) => void;
}) {
  const [tz, setTz] = useState("America/New_York");
  const [generating, setGenerating] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const notFoundCountRef = useRef(0);

  const start = async () => {
    setGenerating(true);
    setError(null);
    notFoundCountRef.current = 0;
    try {
      await startReportGeneration("classroom", { classroomId, date, tz });
      setPolling(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Failed to start generation.");
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!polling) return;
    const interval = setInterval(async () => {
      try {
        const params = new URLSearchParams({
          classroomId: String(classroomId),
          date,
        });
        const r = await fetch(
          `/api/internal/admin/classroom-reports/lookup?${params}`,
        );
        if (r.status === 404) {
          notFoundCountRef.current += 1;
          if (notFoundCountRef.current >= 6) {
            clearInterval(interval);
            setError(
              "Report generation failed — Lambda may have crashed. Check CloudWatch logs.",
            );
            setPolling(false);
            setGenerating(false);
          }
          return;
        }
        if (!r.ok) return;
        notFoundCountRef.current = 0;
        const data: ClassroomReportRow = await r.json();
        if (data.status === "generated") {
          clearInterval(interval);
          onDone(data);
        } else if (data.status === "stale") {
          clearInterval(interval);
          setError("Regeneration failed — showing previous report.");
          setPolling(false);
          setGenerating(false);
          onDone(data);
        } else if (data.status === "failed") {
          clearInterval(interval);
          setError("Report generation failed.");
          setPolling(false);
          setGenerating(false);
        } else if (data.status === "no_data") {
          clearInterval(interval);
          setError("No sessions found for this date.");
          setPolling(false);
          setGenerating(false);
        }
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [polling, classroomId, date, onDone]);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !generating) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Classroom ID</p>
              <p className="font-medium">{classroomId}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Date</p>
              <p className="font-medium">{date}</p>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Timezone</label>
            <Select value={tz} onValueChange={setTz} disabled={generating}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {US_TIMEZONES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {polling && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Running… this may take a few minutes
            </div>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={generating && polling}
          >
            Cancel
          </Button>
          <Button onClick={start} disabled={generating}>
            {generating ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" />
                Generating…
              </>
            ) : (
              "Generate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
